<template>
  <div class="d-flex text-medium-1" @click="onNotificationClick" v-if="entityType">
    <div class="d-flex align-items-start left-section mr-2">
      <div class="notification-icon-wrapper flex-center position-relative">
        <span class="read-indicator" v-if="notification.isUnread"></span>
        <i
          :class="{
            'award-icon': type === 'award',
            'mention-icon': type === 'mention',
            'like-icon': type === 'reaction'
          }"
        ></i>
      </div>
      <div class="reaction-icon-wrapper flex-center" v-if="type === 'reaction'">
        <span class="reaction-icon" :style="emojiStyle"> </span>
      </div>
      <user-short length="30" :user-id="notification.actorId" v-else />
    </div>
    <div class="right-section flex-grow-1">
      <div class="text-medium action-wrapper text-prime">
        <span class="semi-bold text-truncate actor-name" v-no-capture>{{ actor.firstName }}</span>
        <span class="flex-shrink-0" v-if="type === 'award'">
          gave you an <span class="text-bold">award </span>on
          <span class="semi-bold text-capitalize">{{ entityMetaMap[entityType].name }}</span>
        </span>
        <span class="flex-shrink-0" v-else-if="type === 'mention'">
          mentioned you on a
          <span class="semi-bold text-capitalize">{{ entityMetaMap[entityType].name }}</span>
        </span>
        <span class="flex-shrink-0" v-else-if="type === 'reaction'">
          reacted on your
          <span class="semi-bold">{{ activityGroupLabel }}</span>
        </span>
      </div>

      <div class="d-flex">
        <i class="flex-shrink-0 mr-2 entity-icon" :class="entityMetaMap[entityType].icon"></i>
        <div class="bold entity-title text-small-1" v-no-capture>
          <template v-if="entity">
            {{ entity.title }}
          </template>
          <deleted-entity v-else />
        </div>
      </div>
      <slot name="context-content"></slot>
      <div class="text-secondary text-small-1 mt-2 position-relative">
        {{ getReadableDate(notification.createdAt) }}
      </div>
    </div>
  </div>
</template>

<script>
import DeletedEntity from '@/components/notifications/DeletedEntity'
import fetchEmojiData from '@/components/reactions/fetchEmojiData'
import client from '@/constants/client'
import { ENTITY_MAP, ENTITY_TYPE } from '@/constants/entities'
import { GOAL_EVENT_TYPE } from '@/constants/goal'
import MembershipsMixin from '@/mixins/MembershipsMixin'
import emojiToUnicode from '@/utils/emojiToUnicode'
import getEntityTypeFromNotification from '@/utils/getEntityTypeFromNotification'
import showAPIError from '@/utils/showAPIError'
import { mapActions } from 'vuex'
import UserShort from '@/components/UserShort'
import { formatDate, getFormattedTime, MILLIS_IN_A_DAY } from '@/utils/formatDate'
import getToday from '@/utils/getToday'

/**
 * This is the component for rendering a notification
 */
export default {
  name: 'Notification',
  props: {
    /**
     * 'award' | 'mention'
     */
    type: String,
    notification: Object
  },
  data() {
    return {
      entityMetaMap: ENTITY_MAP,
      emojiData: []
    }
  },
  components: {
    UserShort,
    DeletedEntity
  },
  mixins: [MembershipsMixin],
  computed: {
    entityType() {
      return getEntityTypeFromNotification(this.notification)
    },
    eventType() {
      return this.notification.data.event?.type
    },
    activityGroupLabel() {
      switch (this.eventType) {
        case GOAL_EVENT_TYPE.CHECKIN:
          return 'checkin'
        case GOAL_EVENT_TYPE.COMMENT:
          return 'comment'
        case GOAL_EVENT_TYPE.AWARD:
          return 'award'
        default:
          return 'activity'
      }
    },
    entity() {
      const entityTypeKeyMap = {
        [ENTITY_TYPE.GOAL]: 'goal',
        [ENTITY_TYPE.KEY_RESULT]: 'keyResult',
        [ENTITY_TYPE.INITIATIVE]: 'initiative'
      }
      return this.notification.data[entityTypeKeyMap[this.entityType]]
    },
    actor() {
      return this.getAndFetchUser(this.notification.actorId) || {}
    },
    emojiStyle() {
      const unicode = emojiToUnicode(this.notification.data.reactionType)
      const emoji = this.emojiData.find(x => {
        return x.unified.toLowerCase() === unicode.toLowerCase()
      })
      const style = {}
      if (emoji) {
        style.backgroundImage = `url(/emojis/${client.emojiType}/v1/${emoji.image})`
      }
      return style
    }
  },
  methods: {
    ...mapActions('notification', ['readNotification']),
    getReadableDate(dateString) {
      const today = getToday()
      const date = new Date(dateString)
      const difference = (date.getTime() - today.getTime()) / MILLIS_IN_A_DAY
      let day
      if (difference > 0 && difference < 1) {
        day = 'Today'
      } else if (difference > -1) {
        day = 'Yesterday'
      } else {
        day = formatDate(dateString)
      }
      const time = getFormattedTime(dateString)
      return `${day} at ${time}`
    },
    async onNotificationClick(event) {
      try {
        const orgId = this.notification.organizationId
        if (this.notification.isUnread) {
          await this.readNotification({
            orgId,
            id: this.notification.id
          })
        }
        this.$emit('notification-click', { notification: this.notification, event })
      } catch (e) {
        showAPIError(e)
      }
    }
  },
  async mounted() {
    this.emojiData = await fetchEmojiData()
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';

.notification-icon-wrapper {
  @include dimension(21px, 21px);
  background-color: $quinary;
  border-radius: 50%;
  margin-top: 5px;
  margin-right: 4px;
}

.left-section {
  margin-top: -4px;
}

.action-wrapper,
.right-section {
  min-width: 0;
}

.text-bold {
  font-weight: 700;
}

.entity-title {
  @include lineClamp(2);
}

.kr-greyed-icon {
  width: 7px;
  height: 9px;
  margin-top: 6px;
}

.entity-icon {
  width: 11px;
  height: 11px;
  margin-top: 4px;
  background-size: contain;
}

.reaction-icon-wrapper {
  @include dimension(45px, 45px);
  background-color: $quinary;
  border-radius: 50%;
}

.actor-name {
  max-width: 100px;
  flex-shrink: 0;
}

.reaction-icon {
  @include dimension(28px, 28px);
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
}

.like-icon {
  opacity: 0.7;
}

.read-indicator {
  @include dimension(8px, 8px);
  position: absolute;
  background-color: $brand;
  border-radius: 50%;
  left: -15px;
  top: 50%;
  transform: translateY(-50%);
}
</style>
